import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  type SortableTagItem,
  SortableTagList,
} from 'components/SortableTagList';
import { getText } from 'i18n/intl';
import { fieldMappingSelector } from 'slices/field.slice';
import { selectObjectTypesMap } from 'slices/objectTypes.slice';
import { useAppSelector } from 'slices/store';
import { v4 as uuid } from 'uuid';
import {
  FieldSettingType,
  type LocationFieldSetting,
  type SubjectTemplateFieldSetting,
} from '@timeedit/calendar-integration-api-contract';
import {
  Button,
  Modal,
  Popconfirm,
  Typography,
} from '@timeedit/ui-components/antd';
import type { MenuProps } from '@timeedit/ui-components/antd';
import StaticTextModal from './StaticTextModal';
import { getFieldDisplayInfo } from './fieldSettingUtils';

const intlPrefix = 'integration.calendar.sync_settings';

export type TemplateFieldSetting =
  | SubjectTemplateFieldSetting
  | LocationFieldSetting;

type FieldSettingTagItem = SortableTagItem<{
  fieldSetting: TemplateFieldSetting;
}>;

export type TokenOptionsHelpers = {
  addField: (field: TemplateFieldSetting) => void;
  openStaticTextModal: () => void;
};

export type EditTemplateModalProps = {
  title: string;
  open: boolean;
  initialFields: TemplateFieldSetting[];
  onOk: (fields: TemplateFieldSetting[]) => void;
  onCancel: () => void;
  onRemove: () => void;
  getTokenOptions: (helpers: TokenOptionsHelpers) => MenuProps['items'];
};

const EditTemplateModal = ({
  title,
  open,
  initialFields,
  onOk,
  onCancel,
  onRemove,
  getTokenOptions,
}: EditTemplateModalProps) => {
  const [fields, setFields] = useState<TemplateFieldSetting[]>(initialFields);
  const [staticTextModalOpen, setStaticTextModalOpen] = useState(false);

  const objectTypesMap = useAppSelector(selectObjectTypesMap);
  const fieldsMap = useAppSelector(fieldMappingSelector);

  useEffect(() => {
    if (open) {
      setFields(initialFields);
    }
  }, [open, initialFields]);

  const onFieldAdd = useCallback((newFieldSetting: TemplateFieldSetting) => {
    setFields((prev) => [...prev, newFieldSetting]);
  }, []);

  const handleOk = useCallback(() => {
    onOk(fields);
  }, [onOk, fields]);

  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const isEmptyTemplate = fields.length === 0;

  const openStaticTextModal = useCallback(() => {
    setStaticTextModalOpen(true);
  }, []);

  const tokenOptions = useMemo<MenuProps['items']>(() => {
    return getTokenOptions({
      addField: onFieldAdd,
      openStaticTextModal,
    });
  }, [getTokenOptions, onFieldAdd, openStaticTextModal]);

  const sortableItems = useMemo<FieldSettingTagItem[]>(() => {
    return fields.map((fieldSetting) => {
      const field =
        'fieldId' in fieldSetting ? fieldsMap[fieldSetting.fieldId] : undefined;
      const objectType =
        fieldSetting.type === FieldSettingType.objectField
          ? objectTypesMap[fieldSetting.objectTypeId]
          : undefined;
      const displayInfo = getFieldDisplayInfo(fieldSetting, field, objectType);
      return {
        id: fieldSetting.id,
        label: displayInfo.label,
        color: displayInfo.color,
        fieldSetting,
      };
    });
  }, [fields, fieldsMap, objectTypesMap]);

  const handleReorder = useCallback((reorderedItems: FieldSettingTagItem[]) => {
    const newFields = reorderedItems.map((item) => item.fieldSetting);
    setFields(newFields);
  }, []);

  const handleRemove = useCallback((item: FieldSettingTagItem) => {
    setFields((prev) => prev.filter((v) => v.id !== item.id));
  }, []);

  const handleStaticTextOk = useCallback(
    (text: string) => {
      onFieldAdd({
        id: uuid(),
        type: FieldSettingType.freeText,
        text,
      });
      setStaticTextModalOpen(false);
    },
    [onFieldAdd]
  );

  return (
    <>
      <Modal
        title={title}
        open={open}
        onCancel={handleCancel}
        footer={
          <>
            <Button onClick={handleCancel}>{getText('cancel')}</Button>
            {isEmptyTemplate ? (
              <Popconfirm
                title={getText(
                  `${intlPrefix}.form.sync_mode.te_out.subject.remove_empty_template.title`
                )}
                description={getText(
                  `${intlPrefix}.form.sync_mode.te_out.subject.remove_empty_template.content`
                )}
                onConfirm={onRemove}
                okText={getText('ok')}
                cancelText={getText('cancel')}
              >
                <Button type="primary">{getText('ok')}</Button>
              </Popconfirm>
            ) : (
              <Button type="primary" onClick={handleOk}>
                {getText('ok')}
              </Button>
            )}
          </>
        }
      >
        <SortableTagList<{ fieldSetting: TemplateFieldSetting }>
          items={sortableItems}
          onReorder={handleReorder}
          onRemove={handleRemove}
          addMenuItems={tokenOptions}
          tagStyle={{ whiteSpace: 'pre' }}
          addButtonLabel={getText(
            `${intlPrefix}.form.sync_mode.te_out.subject.add_component`
          )}
          previewLabel={getText('preview')}
          renderPreview={(items) => (
            <>
              {items.map((item) => {
                const previewText =
                  item.fieldSetting.type === FieldSettingType.freeText
                    ? item.fieldSetting.text
                    : item.label;
                return (
                  <Typography.Text key={item.id} style={{ whiteSpace: 'pre' }}>
                    {previewText}
                  </Typography.Text>
                );
              })}
            </>
          )}
        />
      </Modal>
      <StaticTextModal
        open={staticTextModalOpen}
        onOk={handleStaticTextOk}
        onCancel={() => setStaticTextModalOpen(false)}
      />
    </>
  );
};

export default EditTemplateModal;
